import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { query } from 'express';
import camelCase from 'lodash-es/camelCase';
import { ForwardedRef, useState } from 'react';

import createLayout from '../../lib/createLayout';
import { getPrefixedProps } from '../../lib/utils';

import AdCard from '../cards/AdCard';
import DocumentHorizontalCard from '../cards/DocumentHorizontalCard';
import DocumentOverCard from '../cards/DocumentOverCard';
import DocumentVerticalCard from '../cards/DocumentVerticalCard';
import List, { ListCardProps, ListProps } from './List';

import styles from '../../styles/lists/layout.module.css';

const CardComponents = {
    Over: DocumentOverCard,
    Horizontal: DocumentHorizontalCard,
    Vertical: DocumentVerticalCard,
};

export interface LayoutListCell extends ListCardProps {
    cardType?: string | null;
}

export interface LayoutListProps extends ListProps {
    layout: string;
    cell?: LayoutListCell | null;
    bigCell?: LayoutListCell | null;
    smallCell?: LayoutListCell | null;
    withAds?: boolean;
}

function LayoutList({
    items = null,
    layout,
    cardImageLoading = undefined,
    cardTheme: globalCardTheme = null,
    cardWith = null,
    cardWithout = null,
    cell = null,
    bigCell = null,
    smallCell = null,
    withBorder = false,
    className = null,
    itemsClassName = null,
    itemClassName = null,
    ref: listRef = null,
    withAds = false,
    adsTargeting = null,
    ...props
}: LayoutListProps & { ref?: ForwardedRef<HTMLDivElement> | null }) {
    const { cells = null } = createLayout(layout, { cell, bigCell, smallCell });
    const getItemStyle = (cell, index) => ({
        gridArea: `item_${index}`,
    });
    const [emptyAds, setEmptyAds] = useState([]);
    const onAdRender = (renderEvent, index) => {
        const { isEmpty = true } = renderEvent || {};
        setEmptyAds((prevEmptyAds) =>
            [...prevEmptyAds, isEmpty ? index : null].filter((i) => i !== null),
        );
    };
    const getCardProps = (defaultCell, index) => {
        const {
            cardType,
            cardSize,
            cardTheme = globalCardTheme,
            cardRatio,
            cardResponsive,
            withBorder: cardWithBorder = withBorder,
            withoutCategory,
            withoutAuthor,
            withoutAuthorImage,
            withoutDuration,
        } = {
            ...getPrefixedProps(cardWith, 'with'),
            ...getPrefixedProps(cardWithout, 'without'),
            ...defaultCell,
        };
        const it = (items || [])[index] || null;
        const isPlaceholder = it === null;
        if (
            withAds &&
            layout === '1_1_1_4' &&
            (index === 2 || index === 4) &&
            emptyAds.indexOf(index) === -1
        ) {
            const { placement } = adsTargeting || {};
            return {
                cardComponent: AdCard,
                cardType,
                cardTheme,
                cardSize,
                cardRatio,
                targeting:
                    placement !== null
                        ? {
                              ...adsTargeting,
                              placementId: placement ? `${placement}:${index}` : null,
                          }
                        : adsTargeting,
                slot: 'list_native',
                isNative: true,
                onRender: (renderEvent) => onAdRender(renderEvent, index),
            };
        }
        return {
            placeholder: isPlaceholder,
            ...it,
            cardComponent: getComponentFromName(CardComponents, cardType),
            size: cardSize,
            theme: cardTheme,
            ratio: cardRatio,
            withBorder: cardWithBorder,
            withoutCategory,
            withoutAuthor,
            withoutAuthorImage,
            withoutDuration,
            responsive: cardResponsive,
        };
    };
    return (
        <List
            {...props}
            className={classNames([
                styles.container,
                styles[camelCase(`layout_${layout}`)],
                className,
            ])}
            items={cells}
            itemsClassName={classNames([styles.items, itemsClassName])}
            itemClassName={classNames([styles.item, itemClassName])}
            cardClassName={styles.card}
            cardWithout={cardWithout}
            cardWith={cardWith}
            itemStyle={getItemStyle}
            cardProps={getCardProps}
            cardImageLoading={cardImageLoading}
            ref={listRef}
        />
    );
}

export default LayoutList;
